import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormHelperText,
  Text,
  useDisclosure,
  useToast,
  Textarea,
  Badge,
  Select,
  HStack,
  Box,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import {
  Plus,
  FileText,
  Trash2,
  RefreshCw,
  Copy,
  Search,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  HelpCircle,
} from 'lucide-react';
import waApi from '../../services/whatsapp/waApi';
import { useDialog } from '../../providers/dialog';

interface WaTemplate {
  id: string;
  name: string;
  status: string;
  category: string;
  language: string;
  components: Array<{
    type: string;
    text?: string;
    format?: string;
    buttons?: Array<{ type: string; text: string }>;
  }>;
  quality_score?: { score: string };
}

const STATUS_MAP: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  APPROVED: { label: 'Aprovado', color: 'green', icon: CheckCircle2 },
  PENDING: { label: 'Pendente', color: 'yellow', icon: Clock },
  REJECTED: { label: 'Rejeitado', color: 'red', icon: XCircle },
  PAUSED: { label: 'Pausado', color: 'orange', icon: AlertTriangle },
  DISABLED: { label: 'Desativado', color: 'gray', icon: XCircle },
};

const CATEGORY_LABELS: Record<string, string> = {
  UTILITY: 'Utilidade',
  MARKETING: 'Marketing',
  AUTHENTICATION: 'Autenticação',
};

// Variáveis disponíveis para montar o corpo do template (issue #34). Os
// templates da Meta usam parâmetros posicionais {{1}}, {{2}}...; no envio o
// sistema preenche o {{1}} com o nome do cliente e o atendente informa os demais.
const TEMPLATE_VARIABLES: Array<{ token: string; nome: string; descricao: string }> = [
  {
    token: '{{1}}',
    nome: 'Nome do cliente',
    descricao:
      'Preenchido automaticamente com o nome do contato no momento do envio. Use sempre como primeira variável.',
  },
  {
    token: '{{2}}',
    nome: '2º parâmetro',
    descricao:
      'Informado pelo atendente ao enviar o template (ex.: nome da empresa, valor, data). Não é preenchido automaticamente.',
  },
  {
    token: '{{3}}',
    nome: '3º parâmetro (e seguintes)',
    descricao:
      'Mais parâmetros livres: {{3}}, {{4}}... Cada um vira um campo a preencher no envio. Numere em sequência, sem pular números.',
  },
];

interface CreateFormData {
  name: string;
  category: string;
  language: string;
  body_text: string;
  footer_text: string;
  header_text: string;
}

const EMPTY_FORM: CreateFormData = {
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  body_text: '',
  footer_text: '',
  header_text: '',
};

function extractBodyText(template: WaTemplate): string {
  const body = template.components.find(c => c.type === 'BODY');
  return body?.text || '';
}

function extractHeaderText(template: WaTemplate): string {
  const header = template.components.find(c => c.type === 'HEADER' && c.format === 'TEXT');
  return header?.text || '';
}

function extractFooterText(template: WaTemplate): string {
  const footer = template.components.find(c => c.type === 'FOOTER');
  return footer?.text || '';
}

function countParams(text: string): number {
  const matches = text.match(/\{\{\d+\}\}/g);
  return matches ? matches.length : 0;
}

export default function WaTemplatesPage() {
  const toast = useToast();
  const showDialog = useDialog();
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Modal de ajuda com as variáveis disponíveis para o corpo do template (#34)
  const variablesModal = useDisclosure();

  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState<CreateFormData>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      const res = await waApi.get('/wa-templates', { params });
      setTemplates(res.data?.data || []);
    } catch {
      toast({ title: 'Erro ao carregar templates', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const filtered = templates.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || extractBodyText(t).toLowerCase().includes(q);
  });

  const handleCreate = async () => {
    if (!form.name || !form.body_text) {
      toast({ title: 'Preencha nome e corpo do template', status: 'warning', duration: 3000 });
      return;
    }

    setCreating(true);
    try {
      const components: any[] = [];

      if (form.header_text) {
        components.push({ type: 'HEADER', format: 'TEXT', text: form.header_text });
      }

      const bodyComponent: any = { type: 'BODY', text: form.body_text };
      const paramCount = countParams(form.body_text);
      if (paramCount > 0) {
        bodyComponent.example = {
          body_text: [Array.from({ length: paramCount }, (_, i) => `exemplo_${i + 1}`)],
        };
      }
      components.push(bodyComponent);

      if (form.footer_text) {
        components.push({ type: 'FOOTER', text: form.footer_text });
      }

      await waApi.post('/wa-templates', {
        name: form.name,
        category: form.category,
        language: form.language,
        components,
      });

      toast({ title: 'Template enviado para aprovação', status: 'success', duration: 3000 });
      setForm(EMPTY_FORM);
      onClose();
      fetchTemplates();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Erro ao criar template';
      toast({ title: msg, status: 'error', duration: 5000 });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (templateName: string) => {
    showDialog({
      title: 'Deletar template',
      status: 'warning',
      description: `Tem certeza que deseja deletar o template "${templateName}"? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        try {
          await waApi.delete(`/wa-templates/${templateName}`);
          toast({ title: 'Template deletado', status: 'success', duration: 3000 });
          fetchTemplates();
        } catch {
          toast({ title: 'Erro ao deletar template', status: 'error', duration: 3000 });
        }
      },
    });
  };

  const copyName = (name: string) => {
    navigator.clipboard.writeText(name);
    toast({ title: 'Nome copiado', status: 'info', duration: 1500 });
  };

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Templates de Mensagem</h1>
          <p className="text-sm text-gray-500">
            Crie e gerencie templates aprovados pela Meta para envio fora da janela de 24h
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<RefreshCw size={14} />}
            onClick={fetchTemplates}
            isLoading={loading}
          >
            Atualizar
          </Button>
          <Button size="sm" colorScheme="green" leftIcon={<Plus size={14} />} onClick={onOpen}>
            Novo Template
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar template..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-green-400 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-green-400 focus:outline-none"
        >
          <option value="">Todos os status</option>
          <option value="APPROVED">Aprovado</option>
          <option value="PENDING">Pendente</option>
          <option value="REJECTED">Rejeitado</option>
        </select>
      </div>

      {/* Lista */}
      {loading && <div className="py-12 text-center text-gray-400">Carregando templates...</div>}
      {!loading && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 py-12 text-center">
          <FileText size={40} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500">Nenhum template encontrado</p>
          <Button size="sm" mt={3} colorScheme="green" variant="outline" onClick={onOpen}>
            Criar primeiro template
          </Button>
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(template => {
            const statusInfo = STATUS_MAP[template.status] || STATUS_MAP.PENDING;
            const StatusIcon = statusInfo.icon;
            const bodyText = extractBodyText(template);
            const headerText = extractHeaderText(template);
            const footerText = extractFooterText(template);
            const params = countParams(bodyText);

            return (
              <div
                key={template.id}
                className="rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <HStack spacing={2} mb={1}>
                      <Text fontSize="sm" fontWeight="700" color="gray.800">
                        {template.name}
                      </Text>
                      <Tooltip label="Copiar nome" hasArrow>
                        <IconButton
                          aria-label="Copiar"
                          icon={<Copy size={12} />}
                          size="xs"
                          variant="ghost"
                          onClick={() => copyName(template.name)}
                        />
                      </Tooltip>
                      <Badge
                        colorScheme={statusInfo.color}
                        fontSize="10px"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <StatusIcon size={10} />
                        {statusInfo.label}
                      </Badge>
                      <Badge variant="subtle" fontSize="10px">
                        {CATEGORY_LABELS[template.category] || template.category}
                      </Badge>
                      <Badge variant="outline" fontSize="10px">
                        {template.language}
                      </Badge>
                      {params > 0 && (
                        <Badge colorScheme="purple" fontSize="10px">
                          {params} param{params > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </HStack>

                    {headerText && (
                      <Text fontSize="xs" color="gray.500" fontWeight="600" mt={1}>
                        {headerText}
                      </Text>
                    )}
                    <Text fontSize="sm" color="gray.600" mt={1} noOfLines={3}>
                      {bodyText || '(sem corpo)'}
                    </Text>
                    {footerText && (
                      <Text fontSize="xs" color="gray.400" mt={1} fontStyle="italic">
                        {footerText}
                      </Text>
                    )}
                  </div>

                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDelete(template.name)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de criação */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Novo Template de Mensagem</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <div className="space-y-4">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Nome do template</FormLabel>
                <Input
                  size="sm"
                  placeholder="ex: atualizacao_fiscal"
                  value={form.name}
                  onChange={e =>
                    setForm(prev => ({
                      ...prev,
                      name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
                    }))
                  }
                />
                <FormHelperText fontSize="xs">
                  Apenas letras minúsculas, números e underscores
                </FormHelperText>
              </FormControl>

              <HStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Categoria</FormLabel>
                  <Select
                    size="sm"
                    value={form.category}
                    onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="UTILITY">Utilidade</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="AUTHENTICATION">Autenticação</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Idioma</FormLabel>
                  <Select
                    size="sm"
                    value={form.language}
                    onChange={e => setForm(prev => ({ ...prev, language: e.target.value }))}
                  >
                    <option value="pt_BR">Português (BR)</option>
                    <option value="en_US">English (US)</option>
                    <option value="es">Español</option>
                  </Select>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="sm">Cabeçalho (opcional)</FormLabel>
                <Input
                  size="sm"
                  placeholder="Título do template"
                  value={form.header_text}
                  onChange={e => setForm(prev => ({ ...prev, header_text: e.target.value }))}
                  maxLength={60}
                />
              </FormControl>

              <FormControl isRequired>
                <HStack justify="space-between" align="center" mb={1}>
                  <FormLabel fontSize="sm" mb={0}>
                    Corpo da mensagem
                  </FormLabel>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="blue"
                    leftIcon={<HelpCircle size={14} />}
                    onClick={variablesModal.onOpen}
                  >
                    Ver variáveis disponíveis
                  </Button>
                </HStack>
                <Textarea
                  size="sm"
                  placeholder="Olá {{1}}, temos uma atualização sobre sua empresa {{2}}."
                  value={form.body_text}
                  onChange={e => setForm(prev => ({ ...prev, body_text: e.target.value }))}
                  rows={4}
                  maxLength={1024}
                />
                <FormHelperText fontSize="xs">
                  Use {'{{1}}'}, {'{{2}}'}, etc. para parâmetros dinâmicos. Máx 1024 caracteres.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Rodapé (opcional)</FormLabel>
                <Input
                  size="sm"
                  placeholder="Rede Especialistas"
                  value={form.footer_text}
                  onChange={e => setForm(prev => ({ ...prev, footer_text: e.target.value }))}
                  maxLength={60}
                />
              </FormControl>

              {/* Preview */}
              {form.body_text && (
                <Box bg="gray.50" borderRadius="lg" p={4}>
                  <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>
                    PREVIEW
                  </Text>
                  <Box bg="white" borderRadius="lg" p={3} shadow="sm" maxW="300px">
                    {form.header_text && (
                      <Text fontSize="sm" fontWeight="700" mb={1}>
                        {form.header_text}
                      </Text>
                    )}
                    <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                      {form.body_text}
                    </Text>
                    {form.footer_text && (
                      <Text fontSize="xs" color="gray.400" mt={2}>
                        {form.footer_text}
                      </Text>
                    )}
                  </Box>
                </Box>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              onClick={handleCreate}
              isLoading={creating}
              loadingText="Enviando..."
            >
              Enviar para aprovação
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de ajuda: variáveis disponíveis no corpo do template (issue #34) */}
      <Modal isOpen={variablesModal.isOpen} onClose={variablesModal.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Variáveis disponíveis</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Use parâmetros no formato <strong>{'{{n}}'}</strong> no corpo do template. Eles são
              substituídos no momento do envio.
            </Text>
            <div className="space-y-3">
              {TEMPLATE_VARIABLES.map(v => (
                <Box key={v.token} bg="gray.50" borderRadius="lg" p={3}>
                  <HStack spacing={2} mb={1}>
                    <Badge colorScheme="blue" fontFamily="mono">
                      {v.token}
                    </Badge>
                    <Text fontSize="sm" fontWeight="600">
                      {v.nome}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.600">
                    {v.descricao}
                  </Text>
                </Box>
              ))}
            </div>
            <Text fontSize="xs" color="gray.400" mt={4}>
              Dica: numere os parâmetros em sequência ({'{{1}}'}, {'{{2}}'}, {'{{3}}'}…), sem pular
              números — a Meta rejeita templates com numeração inconsistente.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={variablesModal.onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
